import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { notasVentaApi } from '../../api/notasVenta'
import { pagosApi, mediosPagoApi } from '../../api/pagos'
import { remitosApi } from '../../api/remitos'
import api from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { hoyISO } from '../../utils/fecha'
import { colors, font, text } from '../../design/tokens'
import { Seccion, Cargando, ErrorMsg, fmtMoney, Titulo, BotonPrimario, ErrorBox, TabsSegmento } from './comunes'

// Procesar la NV desde el celular = el "📦 Enviar paquete a Presea" del
// escritorio en versión de bolsillo. MISMO contrato y MISMO orden que
// handleEnvioCompleto (NotaVentaDetallePage): recibo → enviar NV → remito.
// Lo que acá no está (dirección de envío editada a mano, multi-recibo,
// tarjeta con banco N_BCO) se hace desde el escritorio.
//
// SOLO REMITO (Martin, 30 sep 2026, mismo modo que el modal del escritorio):
// mercadería que viaja SIN factura ni recibo (traslado, reposición,
// muestra). La NV va a Presea solo como vehículo del remito
// (POST /enviar {solo_remito:true} → despacho FORMULARIO='REMITO').

const ENTREGAS = [
  { k: 'domicilio', icono: '🚚', label: 'Envío a domicilio', desc: 'Remito T90 a la dirección del pedido' },
  { k: 'retira',    icono: '🏬', label: 'Retira el cliente', desc: 'Elegí la sucursal donde lo busca' },
  { k: 't80',       icono: '📦', label: 'Varios destinos (T80)', desc: 'La planilla de envío se carga después' },
  { k: 'virtual',   icono: '✨', label: 'Sin entrega', desc: 'Cena, evento o gift card — no despacha' },
]

export default function MovilProcesarNv() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { rolOperativoVentas } = useAuth()

  const [nv, setNv] = useState(null)
  const [error, setError] = useState(null)
  const [errorEnvio, setErrorEnvio] = useState('')
  const [enviando, setEnviando] = useState(false)

  const [entrega, setEntrega] = useState(null)
  const [sucursales, setSucursales] = useState([])
  const [sucursal, setSucursal] = useState('')

  const [medios, setMedios] = useState([])
  const [conRecibo, setConRecibo] = useState(true)
  const [medioId, setMedioId] = useState('')
  const [importe, setImporte] = useState('')
  const [soloRemito, setSoloRemito] = useState(false)

  useEffect(() => {
    notasVentaApi.obtener(id).then(({ data }) => {
      setNv(data)
      // Preselección con criterio: venta sin mercadería → sin entrega.
      setEntrega(data.requiere_entrega === false ? 'virtual' : 'domicilio')
      // Importe sugerido = lo que hay que cobrar (con percepciones si aplican)
      const base = parseFloat(data.monto_a_cobrar ?? data.total) || 0
      const percep = data.percepciones_estimadas?.aplica ? (data.percepciones_estimadas.total || 0) : 0
      const aCobrar = data.monto_a_cobrar && Math.abs(parseFloat(data.monto_a_cobrar) - parseFloat(data.total)) > 0.01
        ? base : base + percep
      const vigentes = (data.pagos || []).filter((p) => !['anulado', 'rechazado', 'devuelto'].includes(p.estado))
      const yaImputado = vigentes.reduce((acc, p) => acc + (parseFloat(p.monto_imputado) || 0), 0)
      setImporte(String(Math.max(0, Math.round((aCobrar - yaImputado) * 100) / 100) || ''))
      if (vigentes.length > 0) setConRecibo(false)
    }).catch(() => setError('No se pudo cargar la nota de venta.'))
    api.get('/sucursales-retiro').then(({ data }) => setSucursales(data || [])).catch(() => setSucursales([]))
    mediosPagoApi.listar(true).then(({ data }) => {
      const lista = data.data || data || []
      setMedios(lista)
    }).catch(() => setMedios([]))
  }, [id])

  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!nv) return <Cargando />

  const rolOperativo = rolOperativoVentas()
  if (!rolOperativo) return <ErrorMsg>Tu rol no puede procesar ventas.</ErrorMsg>

  // NV ya procesada con remito → nada que hacer acá
  if (!nv.es_editable && nv.numero_remito_presea) {
    return (
      <div>
        <Titulo>{nv.numero_lgr}</Titulo>
        <Seccion>
          <p style={{ margin: 0, fontSize: 14, color: colors.textSec, fontFamily: font.sans }}>
            ✓ Esta venta ya está procesada (Factura {nv.numero_presea} · Remito {nv.numero_remito_presea}).
          </p>
        </Seccion>
      </div>
    )
  }

  const pagosVigentes = (nv.pagos || []).filter((p) => !['anulado', 'rechazado', 'devuelto'].includes(p.estado))
  const confirmados = pagosVigentes.filter((p) => ['confirmado', 'acreditado'].includes(p.estado))
  const pendientes = pagosVigentes.filter((p) => p.estado === 'pendiente')

  const totalMostrar = (() => {
    const t = parseFloat(nv.total) || 0
    if (nv.monto_a_cobrar && Math.abs(parseFloat(nv.monto_a_cobrar) - t) > 0.01) return parseFloat(nv.monto_a_cobrar)
    if (nv.percepciones_estimadas?.aplica) return t + (nv.percepciones_estimadas.total || 0)
    return t
  })()

  const medioSel = medios.find((m) => String(m.id) === String(medioId))
  const medioNecesitaBanco = medioSel && ['tarjeta', 'cheque'].includes((medioSel.tipo || '').toLowerCase())

  const recibo = conRecibo && !soloRemito
  // Solo remito no lleva recibo: con cobros vinculados el backend lo rechaza.
  const soloRemitoBloqueado = soloRemito && pagosVigentes.length > 0
  const listo = entrega
    && (entrega !== 'retira' || sucursal)
    && !soloRemitoBloqueado
    && (!recibo || (medioId && parseFloat(importe) > 0 && !medioNecesitaBanco))

  const procesar = async () => {
    if (!listo || enviando) return
    setEnviando(true)
    setErrorEnvio('')
    try {
      // 1) Recibo (nace confirmado — cargarlo ES la confirmación, igual que el modal)
      if (recibo && medioId && parseFloat(importe) > 0) {
        await pagosApi.crear({
          cliente_id: nv.cliente.id,
          fecha: hoyISO(),
          importe: parseFloat(importe),
          medio_pago_id: parseInt(medioId),
          observaciones: `Recibo vinculado a NV ${nv.numero_lgr}`,
          nv_ids: [nv.id],
          confirmar: true,
        })
      }

      // 2) Enviar la NV (factura; el chain sigue solo en Presea)
      if (nv.es_editable) {
        await notasVentaApi.enviar(nv.id, soloRemito ? { solo_remito: true } : undefined)
      }

      // 3) Remito según el tipo de entrega elegido
      const articulos = nv.items.map((it) => ({ articulo_id: it.articulo_id, cantidad: it.cantidad }))
      const base = { cliente_id: nv.cliente.id, nota_venta_id: nv.id, fecha_emision: hoyISO(), articulos }

      if (entrega === 'domicilio') {
        // La dirección del remito: la de envío de la NV si tiene, si no la del
        // cliente. Igual que el modal con "usar dirección de facturación".
        const d = nv.direccion_envio || {}
        const partes = d.calle
          ? [d.receptor_nombre, [d.calle, d.piso].filter(Boolean).join(' '), d.localidad, d.provincia, d.codigo_postal]
          : [nv.cliente?.domicilio, nv.cliente?.localidad, nv.cliente?.provincia, nv.cliente?.codigo_postal]
        await remitosApi.crear({ ...base, transporte: '90', observaciones_raw: partes.filter(Boolean).join(', ') })
      } else if (entrega === 'retira') {
        await remitosApi.crear({ ...base, transporte: '90', retira: true, sucursal_retiro: sucursal || null })
      } else if (entrega === 't80') {
        await remitosApi.crear({ ...base, transporte: '80' })
      } else if (entrega === 'virtual') {
        await remitosApi.crear({ ...base, transporte: '0' })
      }

      navigate(`/m/ventas/${nv.id}`, { replace: true })
    } catch (err) {
      const data = err.response?.data
      const detalle = data?.errors ? Object.values(data.errors).flat().join(' · ') : null
      setErrorEnvio(detalle || data?.message || 'No se pudo procesar.')
      setEnviando(false)
    }
  }

  return (
    <div>
      <Titulo>Procesar {nv.numero_lgr}</Titulo>

      <ErrorBox>{errorEnvio}</ErrorBox>

      <Seccion style={{ marginTop: 0 }}>
        <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15, color: colors.text }}>{nv.cliente?.nombre}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 6 }}>
          <span style={{ fontSize: text.sec, color: colors.textSec }}>
            {nv.items?.length || 0} renglones · Total a cobrar
          </span>
          <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 16, color: colors.text }}>
            {fmtMoney(totalMostrar)}
          </span>
        </div>
        {nv.percepciones_estimadas?.aplica && (
          <p style={{ margin: '4px 0 0', fontSize: 11.5, color: colors.textMeta, fontFamily: font.sans }}>
            Incluye percepciones estimadas ({fmtMoney(nv.percepciones_estimadas.total)}).
          </p>
        )}
      </Seccion>

      {/* ── Qué se emite ── */}
      {nv.es_editable && (
        <Seccion titulo="¿Qué se manda a Presea?">
          <TabsModo value={soloRemito} onChange={setSoloRemito} />
          <p style={{ margin: '8px 2px 0', fontSize: 12, color: colors.textSec, fontFamily: font.sans, lineHeight: 1.45 }}>
            {soloRemito
              ? '🚚 Solo el remito — la mercadería viaja SIN factura ni recibo (traslado, reposición, muestra).'
              : 'Factura y remito (y el recibo si lo cargás abajo).'}
          </p>
          {soloRemitoBloqueado && (
            <p style={{ margin: '6px 2px 0', fontSize: 12, color: colors.warn, fontFamily: font.sans }}>
              Esta venta tiene {pagosVigentes.length} cobro(s) vinculado(s): solo remito no lleva recibo. Desvinculalos desde el escritorio o mandá factura y remito.
            </p>
          )}
        </Seccion>
      )}

      {/* ── Entrega ── */}
      <Seccion titulo="¿Cómo se entrega?">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ENTREGAS.map((e) => {
            const on = entrega === e.k
            return (
              <button key={e.k} onClick={() => setEntrega(e.k)} style={{ ...opcion, borderColor: on ? colors.primary : colors.border, background: on ? colors.linkBg : colors.bg }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{e.icono}</span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={{ display: 'block', fontFamily: font.sans, fontWeight: 600, fontSize: 14, color: colors.text }}>{e.label}</span>
                  <span style={{ display: 'block', fontSize: 12, color: colors.textSec, fontFamily: font.sans }}>{e.desc}</span>
                </span>
                <span style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${on ? colors.primary : colors.border}`, background: on ? colors.primary : 'transparent', flexShrink: 0, boxShadow: on ? `inset 0 0 0 3px ${colors.linkBg}` : 'none' }} />
              </button>
            )
          })}
        </div>

        {entrega === 'domicilio' && (
          <p style={notaDir}>
            📍 {(() => {
              const d = nv.direccion_envio || {}
              if (d.calle) return [d.calle, d.piso, d.localidad, d.provincia].filter(Boolean).join(', ')
              return [nv.cliente?.domicilio, nv.cliente?.localidad, nv.cliente?.provincia].filter(Boolean).join(', ') || 'Sin dirección cargada'
            })()}
            <span style={{ color: colors.textMeta }}> — para cambiarla, editá la NV en el escritorio.</span>
          </p>
        )}

        {entrega === 'retira' && (
          <select value={sucursal} onChange={(e) => setSucursal(e.target.value)} style={select}>
            <option value="">¿En qué sucursal lo retira?</option>
            {sucursales.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}{s.direccion ? ` — ${s.direccion}` : ''}</option>
            ))}
          </select>
        )}
      </Seccion>

      {/* ── Cobro ── (solo remito no lleva recibo) */}
      {!soloRemito && <Seccion titulo="Cobro">
        {confirmados.length > 0 && (
          <div style={{ ...cobroCard, background: colors.okBg, borderColor: '#bbf7d0' }}>
            {confirmados.map((p) => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: colors.ok, fontFamily: font.sans }}>
                <span>✓ {p.medio_pago || 'Recibo'} — se incluye en el paquete</span>
                <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(p.monto_imputado)}</span>
              </div>
            ))}
          </div>
        )}
        {pendientes.length > 0 && (
          <div style={{ ...cobroCard, background: colors.warnBg, borderColor: '#fcd34d' }}>
            {pendientes.map((p) => (
              <div key={p.id} style={{ fontSize: 12.5, color: colors.warn, fontFamily: font.sans }}>
                ⏳ {p.medio_pago || 'Cobro'} {fmtMoney(p.monto_imputado)} pendiente — confirmalo en Recibos para que viaje.
              </div>
            ))}
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 2px', fontFamily: font.sans, fontSize: 14, color: colors.text }}>
          <input type="checkbox" checked={conRecibo} onChange={(e) => setConRecibo(e.target.checked)} style={{ width: 18, height: 18 }} />
          Cargar un recibo ahora
        </label>

        {conRecibo && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <select value={medioId} onChange={(e) => setMedioId(e.target.value)} style={select}>
              <option value="">Medio de pago…</option>
              {medios.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre || m.descripcion}</option>
              ))}
            </select>
            {medioNecesitaBanco && (
              <p style={{ margin: 0, fontSize: 12, color: colors.warn, fontFamily: font.sans }}>
                Tarjeta y cheque necesitan los datos del banco (cupón, autorización) — cargá ese recibo desde el escritorio.
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, color: colors.textSec }}>$</span>
              <input
                type="number" inputMode="decimal" min="0" step="0.01"
                value={importe}
                onChange={(e) => setImporte(e.target.value)}
                placeholder="Importe"
                style={{ ...select, textAlign: 'right', fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' }}
              />
            </div>
          </div>
        )}
        {!conRecibo && confirmados.length === 0 && (
          <p style={{ margin: '2px 2px 0', fontSize: 12, color: colors.textMeta, fontFamily: font.sans }}>
            La factura sale sin recibo — el cobro se carga después desde Recibos.
          </p>
        )}
      </Seccion>}

      <BotonPrimario onClick={procesar} disabled={!listo || enviando} style={{ marginTop: 14, opacity: !listo || enviando ? 0.5 : 1 }}>
        {enviando ? 'Procesando…' : soloRemito ? '🚚 Enviar solo el remito a Presea' : '📦 Enviar paquete a Presea'}
      </BotonPrimario>
      <p style={{ fontSize: 12, color: colors.textMeta, textAlign: 'center', margin: '8px 0 0', fontFamily: font.sans }}>
        {soloRemito
          ? 'Remito sin factura. Cuando vuelva el número de Presea, lo imprimís y lo confirmás desde el detalle.'
          : `Factura + remito${recibo ? ' + recibo' : ''} en una sola cadena. El avance se ve en el detalle.`}
      </p>
    </div>
  )
}

const opcion = {
  display: 'flex', alignItems: 'center', gap: 12, width: '100%', boxSizing: 'border-box',
  padding: '12px 14px', borderRadius: 12, border: `1.5px solid ${colors.border}`,
  cursor: 'pointer', WebkitTapHighlightColor: 'rgba(15,23,42,0.06)',
}
const notaDir = { margin: '10px 2px 0', fontSize: 12.5, color: colors.textSec, fontFamily: font.sans, lineHeight: 1.45 }
const select = {
  width: '100%', boxSizing: 'border-box', padding: '11px 12px', border: `1px solid ${colors.border}`,
  borderRadius: 10, fontSize: 14.5, fontFamily: font.sans, color: colors.text, background: colors.bg, outline: 'none',
}
const cobroCard = { border: '1px solid', borderRadius: 10, padding: '9px 12px', marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 4 }

function TabsModo({ value, onChange }) {
  return (
    <TabsSegmento
      options={[{ k: 'completo', label: '📦 Factura y remito' }, { k: 'solo', label: '🚚 Solo remito' }]}
      value={value ? 'solo' : 'completo'}
      onChange={(v) => onChange(v === 'solo')}
    />
  )
}
